import React, { useState, useEffect } from "react";
import { X, Clock, AlertCircle } from "lucide-react";

export default function NotificationBar() {
  const [isVisible, setIsVisible] = useState(true);
  const [badgeData, setBadgeData] = useState({
    title: "",
    message: "",
    date: "",
    isActive: false,
  });
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);

  // Cache key and duration (1 hour = 3600000ms)
  const CACHE_KEY = "notification_data";
  const CACHE_DURATION = 60 * 60 * 1000;

  const handleWhatsApp = () => {
    window.open("https://wa.me/919934743600", "_blank");
  };

  // Check if cached data is still valid
  const getCachedData = () => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.data;
      }
    } catch (error) {
      console.error("Error reading cache:", error);
    }
    return null;
  };

  // Save data to cache
  const setCachedData = (data) => {
    try {
      const cacheEntry = {
        data: data,
        timestamp: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheEntry));
    } catch (error) {
      console.error("Error saving to cache:", error);
    }
  };

  // Google Sheets fetch with caching
  const fetchFromGoogleSheets = async () => {
    try {
      // Check cache first
      const cachedData = getCachedData();
      if (cachedData) {
        setBadgeData(cachedData);
        setHasData(
          cachedData.isActive && cachedData.title && cachedData.message
        );
        setLoading(false);
        return;
      }

      const SHEET_ID = process.env.REACT_APP_GOOGLE_SHEET_ID;
      const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

      if (!SHEET_ID || !API_KEY) {
        setHasData(false);
        setLoading(false);
        return;
      }

      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/Sheet1!A2:D2?key=${API_KEY}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check if we have valid data
      if (data.values && data.values[0] && data.values[0].length >= 3) {
        const [title, message, date, isActive] = data.values[0];

        // Only proceed if we have the essential data
        if (title && message && date) {
          const notificationData = {
            title: title.trim(),
            message: message.trim(),
            date: date.trim(),
            isActive:
              isActive !== "FALSE" &&
              isActive !== "false" &&
              isActive !== false,
          };

          setBadgeData(notificationData);
          setCachedData(notificationData);
          setHasData(notificationData.isActive);
        } else {
          setHasData(false);
        }
      } else {
        setHasData(false);
      }
    } catch (error) {
      console.error("Error fetching notification data:", error);
      setHasData(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFromGoogleSheets();
  }, []);

  const calculateDaysLeft = (dateString) => {
    const deadline = new Date(dateString);
    const today = new Date();
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? `${diffDays} days left` : "Overdue";
  };

  const getDaysLeftColor = (dateString) => {
    const deadline = new Date(dateString);
    const today = new Date();
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return "red"; // Overdue
    if (diffDays <= 7) return "red"; // Less than a week
    if (diffDays <= 30) return "amber"; // Less than a month
    return "blue"; // More than a month
  };

  const isDeadlinePassed = (dateString) => {
    const deadline = new Date(dateString);
    const today = new Date();
    deadline.setHours(23, 59, 59, 999);
    return today > deadline;
  };

  // Don't render anything if loading, no data, notification is inactive
  if (loading || !hasData || !isVisible || !badgeData.isActive)
    return <div className="mt-16"></div>;

  const urgencyColor = getDaysLeftColor(badgeData.date);
  const colorClasses = {
    red: {
      bg: "bg-red-50 border-red-200",
      text: "text-red-900",
      badge: "bg-red-100 text-red-800",
      icon: "text-red-600",
      button: "bg-red-600 hover:bg-red-700 text-white",
    },
    amber: {
      bg: "bg-amber-50 border-amber-200",
      text: "text-amber-900",
      badge: "bg-amber-100 text-amber-800",
      icon: "text-amber-600",
      button: "bg-amber-600 hover:bg-amber-700 text-white",
    },
    blue: {
      bg: "bg-blue-50 border-blue-200",
      text: "text-blue-900",
      badge: "bg-blue-100 text-blue-800",
      icon: "text-blue-600",
      button: "bg-blue-600 hover:bg-blue-700 text-white",
    },
  };

  const colors = colorClasses[urgencyColor];

  return (
    <div
      className={`w-full mt-16 ${colors.bg} border-b ${
        colors.bg.includes("border") ? "" : "border-gray-200"
      } shadow-sm`}
    >
      <div className="px-3 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-2 py-3">
          {/* Left side - Icon and message */}
          <div className="flex items-center flex-1 min-w-0 space-x-2 sm:space-x-3">
            <div className="flex-shrink-0">
              {urgencyColor === "red" ? (
                <AlertCircle
                  className={`w-4 h-4 sm:w-5 sm:h-5 ${colors.icon} animate-pulse`}
                />
              ) : (
                <Clock className={`w-4 h-4 sm:w-5 sm:h-5 ${colors.icon}`} />
              )}
            </div>

            <div className="flex flex-col space-y-0.5 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4 min-w-0 flex-1">
              <span
                className={`text-xs sm:text-sm font-semibold ${colors.text} truncate`}
              >
                {badgeData.title}
              </span>
              <span
                className={`text-xs sm:text-sm ${colors.text} opacity-80 break-words leading-tight`}
              >
                {badgeData.message}
              </span>
            </div>
          </div>

          {/* Right side - Days left and actions */}
          <div className="flex items-center flex-shrink-0 space-x-2 sm:space-x-3">
            <span
              className={`inline-flex items-center px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-xs font-medium ${colors.badge} whitespace-nowrap`}
            >
              {calculateDaysLeft(badgeData.date)}
            </span>

            <button
              onClick={handleWhatsApp}
              className={`hidden sm:inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${colors.button} whitespace-nowrap`}
            >
              Take Action
            </button>

            <button
              onClick={() => setIsVisible(false)}
              className={`p-1 sm:p-1.5 rounded-md ${colors.text} hover:bg-black/5 transition-colors flex-shrink-0`}
              aria-label="Dismiss notification"
            >
              <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
